import CustomModal from 'components/molecules/Modal'
import React, { useCallback, useRef, useState } from 'react'

/**
 * Stable modal component identity across parent re-renders (e.g. SSE, Formik).
 * Previously a new `const Modal = () => ...` each render made React remount the modal tree and lose child state.
 */
export default function useModal({ callback, responsive, content, padding, title, closeable, height, width, customClass, dimensions, description, headerRight }) {
  const [open, setOpen] = useState(false)

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  const openModal = useCallback(() => {
    setOpen(true)
  }, [])

  const payloadRef = useRef({})
  payloadRef.current = {
    open,
    close,
    callback,
    responsive,
    content,
    padding,
    title,
    closeable,
    height,
    width,
    customClass,
    dimensions,
    description,
    headerRight,
  }

  const stableModalRef = useRef(null)
  if (!stableModalRef.current) {
    stableModalRef.current = function StableModal(props) {
      const p = payloadRef.current
      const childrenWithProps = React.Children.map(p.content, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { close: p.close })
        }
        return child
      })
      return (
        <CustomModal
          responsive={p.responsive}
          callback={p.callback}
          dimensions={p.dimensions}
          customClass={p.customClass}
          padding={p.padding}
          height={p.height}
          width={p.width}
          closeable={p.closeable}
          open={p.open}
          title={p.title}
          onClose={p.close}
          description={p.description}
          headerRight={p.headerRight}
          {...props}
        >
          {childrenWithProps}
        </CustomModal>
      )
    }
    stableModalRef.current.displayName = 'StableModal'
  }

  return [stableModalRef.current, openModal, close]
}
